import type { SearchResultJob } from '@seek/chalice-types';
import { memo, useCallback } from 'react';
import { useDispatch, useSelector } from 'src/store/react';
import striptags from 'striptags';
import type { SolMetadata } from '@seek/sol-js';
import { useSplitViewAccessibilityLinks } from 'src/components/SearchResultPage/SplitView/AccessabilityLinks/AccessabilityLinks';
import {
  useIsSplitViewEnabled,
  useIsUnifiedSrpSplitViewExperiment1Enabled,
} from 'src/store/featureFlags/hooks';
import SolImpressionTracker from 'src/modules/solTracker/SolImpressionTracker';
import { useEnrichedLocation } from 'src/modules/enriched-location';
import { useIsJobExternal } from 'src/modules/qualified-location';
import { setSelectedJobIdAction } from 'src/store/results/results';
import {
  selectFeatureFlag,
  selectLocation,
  selectZoneFeatures,
} from 'src/store/selectors';

import { persistJobUrl } from '../../SplitView/helpers';
import JobListItemView, { type JobListItemViewProps } from './JobListItemView';
import { highlightFromHTML } from './helpers';
import useJDPNavLinkProps from './hooks/useJDPNavLinkProps';
import useJobViewModel from './hooks/useJobViewModel';
import useSalaryDisplayLabel from './hooks/useSalaryDisplayLabel';
import isNewTabEvent from '../../../../modules/is-new-tab-event';
import SaveJob from '../../../SaveJob/SaveJob.tsx';
import { CompanyLink } from '../CompanyLink.tsx';
import LocationLink from './LocationLink/LocationLink.tsx';
import ClassificationLinks from './ClassificationLinks/ClassificationLinks.tsx';
import { Link, TextLink } from '../../../NavLink/NavLink.tsx';

enum SearchDisplayStyle {
  GreyBackground = 'A',
}

interface JobListItemProps {
  isSelected?: boolean;
  job: SearchResultJob;
  setCurrentJob?: (job: SearchResultJob) => void;
}

const useJobLinkOnClickCallback = ({
  jdpNavLinkProps: { onBeforeNavigation },
  onClick,
}: Pick<JobListItemViewProps, 'onClick' | 'jdpNavLinkProps'>) => {
  const shouldAddOverlayLink = useIsUnifiedSrpSplitViewExperiment1Enabled();
  return useCallback(
    (e) => {
      if (shouldAddOverlayLink) {
        if (!isNewTabEvent(e)) {
          onClick?.(e);
        } else {
          onBeforeNavigation?.();
        }
      }
    },
    [onBeforeNavigation, onClick, shouldAddOverlayLink],
  );
};

const JobListItem = ({ isSelected, job, setCurrentJob }: JobListItemProps) => {
  const dispatch = useDispatch();

  const isSplitView = useIsSplitViewEnabled();
  const { SHOW_JOBCARD_TEASER_IN_SPLITVIEW } = useSelector(selectZoneFeatures);
  const shouldOpenApplyInNewTab = useSelector(
    selectFeatureFlag('shouldOpenApplyInNewTab'),
  );
  const jdpNavLinkProps = useJDPNavLinkProps(job);

  const {
    bulletPoints,
    classification,
    currencyLabel,
    id,
    isPremium,
    joraImpressionTrackingUrl,
    listingDate,
    listingDateDescription,
    locationMatch,
    salary,
    teaser,
    title,
    unifiedListingDate,
    unifiedListingDateDescription,
    unifiedLocation,
    workType,
  } = useJobViewModel(job);

  const isJobExternal = useIsJobExternal(job.jobLocation?.countryCode);
  const currentLocation = useSelector(selectLocation);
  const srcLogo = job.branding?.assets?.logo?.strategies?.serpLogo;
  const salaryDisplayLabel = useSalaryDisplayLabel({
    salary,
    currencyLabel,
  });
  // This will make sure the URL that push to `history` is the same as the URL on the Job Card's Title
  const enrichedLocation =
    useEnrichedLocation(jdpNavLinkProps)?.href || jdpNavLinkProps.location.href;

  const { focusReturnToSearchLink } = useSplitViewAccessibilityLinks();
  const handleClick = (selectedJob: SearchResultJob) =>
    isSplitView
      ? (e: React.MouseEvent) => {
          // setCurrentJob is defined only in split view
          if (setCurrentJob) {
            e.preventDefault();

            setCurrentJob(selectedJob);
            const jobId = selectedJob.id.toString();
            // mark link visited
            history.replaceState({}, '', enrichedLocation);
            // persist Job ID
            persistJobUrl({
              currentLocation,
              job,
            });
            dispatch(setSelectedJobIdAction(jobId));
            focusReturnToSearchLink();
          }
        }
      : undefined;

  const onClick = handleClick(job);

  const jobLinkOnClickCallback = useJobLinkOnClickCallback({
    jdpNavLinkProps,
    onClick,
  });

  const saveJob = (
    <SaveJob
      withAppliedTick
      jobId={String(job.id)}
      jobTracking={job.tracking}
    />
  );

  const isSplitViewExperiment1Running =
    useIsUnifiedSrpSplitViewExperiment1Enabled();

  return (
    <JobListItemView
      aria-label={striptags(title)}
      aria-selected={isSelected}
      brandingId={job.branding?.id ?? ''}
      bulletPoints={bulletPoints}
      companyLink={<CompanyLink job={job} />}
      classificationLinks={
        <ClassificationLinks classifications={classification} />
      }
      id={id}
      isPremium={isPremium}
      isStandOut={
        job.displayStyle?.search === SearchDisplayStyle.GreyBackground // TODO: Once seek-refine-jobs is merged in, we can update useJobViewModel().
      }
      isSelected={isSelected}
      isSplitView={isSplitView}
      isSplitViewExperiment1Running={isSplitViewExperiment1Running}
      isShowJobCardTeaserInSplitView={SHOW_JOBCARD_TEASER_IN_SPLITVIEW}
      jdpNavLinkProps={jdpNavLinkProps}
      joraImpressionTrackingUrl={joraImpressionTrackingUrl}
      listingDate={{
        unifiedListingDate,
        listingDate,
      }}
      listingDateDescription={{
        unifiedListingDateDescription,
        listingDateDescription,
      }}
      locationLink={
        unifiedLocation ? (
          <LocationLink
            unifiedLocation={unifiedLocation}
            rel={isJobExternal ? 'nofollow' : undefined}
          />
        ) : null
      }
      locationMatch={locationMatch}
      shouldOpenApplyInNewTab={shouldOpenApplyInNewTab}
      shouldRenderLogo={srcLogo !== undefined}
      srcLogo={srcLogo}
      onClick={onClick}
      saveJob={saveJob}
      salary={salary}
      salaryDisplayLabel={salaryDisplayLabel}
      teaser={teaser}
      jobTitle={highlightFromHTML({ html: title })}
      jobLinkOnClickCallback={jobLinkOnClickCallback}
      workType={workType}
      Link={Link}
      TextLink={TextLink}
    />
  );
};

const withSolImpressionTracker = ({
  isSelected,
  job,
  setCurrentJob,
  solMetadata,
}: JobListItemProps & { solMetadata: SolMetadata }) => (
  <SolImpressionTracker jobId={job.id} solMetadata={solMetadata}>
    <JobListItem
      isSelected={isSelected}
      job={job}
      setCurrentJob={setCurrentJob}
    />
  </SolImpressionTracker>
);
export default memo(withSolImpressionTracker);
